/**
 * GalleryCard Component
 * Notion-style card for gallery view with cover images and property badges
 */

import React, { useState } from 'react';
import { Box, Text, Group, Stack, Image, ActionIcon, Tooltip } from '@mantine/core';
import { IconDots, IconHeart, IconHeartFilled, IconPlus } from '@tabler/icons-react';
import { 
  DatabaseItem, 
  CardCover, 
  CardDisplayConfig,
  PropertyDefinition,
  PropertyValue
} from '../../types/database/item';
import { PropertyBadge } from './PropertyBadge';
import { getPropertyColor } from '../../utils/propertyUtils';

interface GalleryCardProps {
  item: DatabaseItem;
  displayConfig: CardDisplayConfig;
  properties: PropertyDefinition[];
  onClick?: () => void;
  onLike?: () => void;
  liked?: boolean;
}

export const GalleryCard: React.FC<GalleryCardProps> = ({
  item,
  displayConfig,
  properties,
  onClick,
  onLike,
  liked = false,
}) => {
  const [imageError, setImageError] = useState(false);
  
  // Get visible properties
  const visibleProperties = properties.filter(
    p => displayConfig.visiblePropertyIds.includes(p.id)
  );
  
  // Get property value helper
  const getValue = (propertyId: string): unknown => {
    const prop = item.properties.find(p => p.propertyId === propertyId);
    return prop?.value ?? null;
  };
  
  // Render cover
  const renderCover = () => {
    if (!displayConfig.showCover) return null;
    
    let coverStyle: React.CSSProperties = { height: 150 };
    
    if (item.cover) {
      if (item.cover.type === 'image' && !imageError) {
        return (
          <Box className="gallery-card-cover">
            <Image
              src={item.cover.value}
              alt={item.title}
              h={150}
              fit="cover"
              onError={() => setImageError(true)}
              fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E"
            />
          </Box>
        );
      } else if (item.cover.type === 'color') {
        coverStyle.backgroundColor = item.cover.value;
      } else if (item.cover.type === 'gradient') {
        coverStyle.background = item.cover.value;
      }
    }
    
    return <Box style={coverStyle} className="gallery-card-cover" />;
  };
  
  // Card size styles
  const cardSizeStyles: Record<string, React.CSSProperties> = {
    small: { minHeight: 120 },
    medium: { minHeight: 180 },
    large: { minHeight: 240 },
  };
  
  return (
    <Box
      className="gallery-card"
      style={{
        ...cardSizeStyles[displayConfig.cardSize] || cardSizeStyles.medium,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      {renderCover()}
      
      <Box className="gallery-card-content" p="sm">
        {/* Icon and Title */}
        <Group gap="xs" mb="xs">
          {item.icon && (
            <Text size="lg">{item.icon}</Text>
          )}
          <Text fw={600} size="sm" className="gallery-card-title truncate">
            {item.title || 'Untitled'}
          </Text>
        </Group>
        
        {/* Property Badges */}
        {visibleProperties.length > 0 && (
          <Group gap={6} mt="xs" wrap="wrap">
            {visibleProperties.map(prop => {
              const value = getValue(prop.id);
              if (value === null || value === undefined) return null;
              
              return (
                <PropertyBadge
                  key={prop.id}
                  propertyId={prop.id}
                  propertyName={prop.name}
                  propertyType={prop.type}
                  value={value}
                  color={getPropertyColor(
                    Array.isArray(value) 
                      ? (value[0] as any)?.color 
                      : (prop.options?.[0] as any)?.color
                  )}
                  compact
                />
              );
            })}
          </Group>
        )}
        
        {/* Timestamps */}
        {displayConfig.showTimestamps && (
          <Text size="xs" c="dimmed" mt="xs">
            {item.updatedAt.toLocaleDateString()}
          </Text>
        )}
      </Box>
      
      {/* Hover Actions */}
      <Box className="gallery-card-actions">
        <Tooltip label={liked ? 'Unlike' : 'Like'}>
          <ActionIcon 
            variant="subtle" 
            color={liked ? 'red' : 'gray'}
            onClick={(e) => {
              e.stopPropagation();
              onLike?.();
            }}
          >
            {liked ? <IconHeartFilled size={16} /> : <IconHeart size={16} />}
          </ActionIcon>
        </Tooltip>
        <ActionIcon variant="subtle" color="gray">
          <IconDots size={16} />
        </ActionIcon>
      </Box>
    </Box>
  );
};

// Quick Add Card (for adding new items)
export const QuickAddCard: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Box
    className="gallery-card quick-add-card"
    onClick={onClick}
    style={{ minHeight: 180 }}
  >
    <Box className="gallery-card-content" p="sm">
      <Group justify="center" h={150}>
        <Stack align="center" gap="xs">
          <Box className="quick-add-icon">
            <IconPlus size={24} />
          </Box>
          <Text size="sm" c="dimmed">Add a card</Text>
        </Stack>
      </Group>
    </Box>
  </Box>
);

export default GalleryCard;